import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, Code2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface ProjectCardProps {
  project: any;
  index: number;
}

export function ProjectCard({ project, index }: ProjectCardProps) {
  const { data: media } = useQuery<any[]>({
    queryKey: ["media", "PROJECT", project.id],
    queryFn: async () => {
      const res = await fetch(`/api/media/PROJECT/${project.id}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!project.id,
  });

  const coverUrl =
    project.coverImage ||
    media?.find((m) => m.isFeatured)?.url ||
    media?.[0]?.url;

  return (
    <Link href={`/work/${project.slug}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className="group relative rounded-2xl overflow-hidden bg-card border border-border/50 cursor-pointer h-full flex flex-col"
      >
        <div className="aspect-[4/3] bg-muted/30 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent z-10"></div>
          <div className="absolute top-4 right-4 z-20">
            <div className="rounded-full bg-background/80 backdrop-blur-md px-3 py-1 text-xs font-medium text-foreground">
              {project.category}
            </div>
          </div>
          <div className="w-full h-full bg-gradient-to-br from-blue-900/40 to-purple-900/40 group-hover:scale-105 transition-transform duration-500 flex items-center justify-center">
            {coverUrl ? (
              <img src={coverUrl} alt={project.title} className="w-full h-full object-cover" />
            ) : (
              <Code2 className="h-16 w-16 text-white/20" />
            )}
          </div>
        </div>
        <div className="p-6 flex-1 flex flex-col relative z-20 -mt-8 bg-card border-t border-border/10">
          <h3 className="font-display text-xl font-bold mb-2 group-hover:text-primary transition-colors">
            {project.title}
          </h3>
          <p className="text-muted-foreground text-sm flex-1">{project.description}</p>
          <div className="mt-4 flex items-center text-sm font-medium text-primary">
            Read Case Study{" "}
            <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
